/**
 * ListCardDiscoveryPanel - List wrapper over the shared `CardSearchPanel`
 */
import type { ReactElement } from 'react';
import { useState } from 'react';

import CardSearchPanel from '@/components/CardSearchPanel/CardSearchPanel';
import type { MinimalCard } from '@/services/cardDatabase';

import { useAddCardToList } from '../hooks/api';
import ListDiscoveryRow from './ListDiscoveryRow';

export interface ListCardDiscoveryPanelProps {
  listId: string;
  /** scryfall_ids already on the list, for the "In list" marker. */
  listedScryfallIds: Set<string>;
  onClose: () => void;
}

export default function ListCardDiscoveryPanel({ listId, listedScryfallIds, onClose }: ListCardDiscoveryPanelProps): ReactElement {
  const addCard = useAddCardToList();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleAdd = async (card: MinimalCard) => {
    // List entries key off oracle_id for cross-edition checks, so index gaps are not addable.
    if (!card.oracle_id) return;

    setPendingId(card.id);
    try {
      await addCard({
        list_id: listId,
        oracle_id: card.oracle_id,
        name: card.name,
        scryfall_id: card.id,
        quantity: 1,
        priority: 'medium',
      });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <CardSearchPanel
      onClose={onClose}
      onActivate={handleAdd}
      emptyHint="Use arrow keys to navigate, Enter to add to this list"
      renderRow={({ card, isHighlighted, onMouseEnter }) => (
        <ListDiscoveryRow
          card={card}
          listedScryfallIds={listedScryfallIds}
          isPending={pendingId === card.id}
          onAdd={handleAdd}
          isHighlighted={isHighlighted}
          onMouseEnter={onMouseEnter}
        />
      )}
    />
  );
}
